// This file will contain the new Svelte 5 store for managing the shopping cart state.
// Content will be added in the following steps of the migration plan.

import { bubbleEvent, bubbleState, setBubbleInstance } from "./state-publisher";

const CART_STORAGE_KEY = "cart-fast";

function createCartStore() {
  let bubbleInstance = null;

  const store = $state({
    lineItems: [],
    currency: "USD",
    taxInclusive: false,
    showTax: true,
    buttonType: "default",
    buttonText: "Checkout",
    itemsIndicator: "default",
    isCartOpen: false,
  });

  // --- DERIVED STATE ---
  const lineItemsWithTax = $derived(
    store.lineItems.map((item) => {
      const baseAmount = (Number(item.unitAmount) || 0) * (item.units || 1);
      const taxPercentage = item.taxPercentage || 0;

      if (store.taxInclusive) {
        // Tax is already included in unitAmount
        const taxAmount = baseAmount - baseAmount / (1 + taxPercentage);
        const subtotalAmount = baseAmount - taxAmount;
        return {
          ...item,
          subtotal: subtotalAmount,
          tax: taxAmount,
          total: baseAmount,
        };
      } else {
        // Tax needs to be added
        const taxAmount = baseAmount * taxPercentage;
        return {
          ...item,
          subtotal: baseAmount,
          tax: taxAmount,
          total: baseAmount + taxAmount,
        };
      }
    })
  );

  const subtotal = $derived(
    lineItemsWithTax.reduce((sum, item) => sum + item.subtotal, 0)
  );
  const tax = $derived(
    lineItemsWithTax.reduce((sum, item) => sum + item.tax, 0)
  );
  const total = $derived(
    lineItemsWithTax.reduce((sum, item) => sum + item.total, 0)
  );
  const itemCount = $derived(store.lineItems.length);
  const isEmpty = $derived(itemCount === 0);

  // --- HELPERS ---
  function saveToLocalStorage() {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        // Only persist the essential state. Derived values will be recalculated.
        const cartData = {
          lineItems: store.lineItems,
        };
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
      } catch (error) {
        console.warn("Failed to save cart to localStorage:", error);
      }
    }
  }

  function loadFromLocalStorage() {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const storedData = localStorage.getItem(CART_STORAGE_KEY);
        if (storedData) {
          const cartData = JSON.parse(storedData);
          // Set the state from the loaded data.
          store.lineItems = cartData.lineItems || [];
        }
      } catch (error) {
        console.warn("Failed to load cart from localStorage:", error);
      }
    }
  }

  function publishState() {
    if (!bubbleInstance) return;
    const cartData = { subtotal, tax, total, lineItems: store.lineItems };
    bubbleState("cart_total", total);
    bubbleState("cart_subtotal", subtotal);
    bubbleState("cart_tax", tax);
    bubbleState("cart_line_items", store.lineItems);
    bubbleState("cart", cartData);
    bubbleEvent("cart_updated");
  }

  const actions = {
    init(props) {
      if (props.bubbleInstance) {
        bubbleInstance = props.bubbleInstance;
        setBubbleInstance(props.bubbleInstance); // Also set it in the publisher module
      }
      store.currency = props.currency ?? "USD";
      store.taxInclusive = props.taxInclusive ?? false;
      store.showTax = props.showTax ?? true;
      store.buttonType = props.buttonType ?? "default";
      store.buttonText = props.buttonText ?? "Checkout";
      store.itemsIndicator = props.itemsIndicator ?? "default";

      loadFromLocalStorage();
      publishState();
    },

    addItem(newItem) {
      if (!newItem.id) {
        console.warn("Item must have an id");
        return;
      }

      const existingItem = store.lineItems.find(
        (item) => item.id === newItem.id
      );
      if (existingItem) {
        this.incrementItem(existingItem);
      } else {
        store.lineItems = [
          ...store.lineItems,
          { ...newItem, units: newItem.units || 1 },
        ];
      }
      store.isCartOpen = true;
      saveToLocalStorage();
      publishState();
    },

    incrementItem(item) {
      const itemId = item.id;
      store.lineItems = store.lineItems.map((item) =>
        item.id === itemId ? { ...item, units: (item.units || 0) + 1 } : item
      );
      saveToLocalStorage();
      publishState();
    },

    decrementItem(item) {
      const itemId = item.id;
      store.lineItems = store.lineItems.map((item) =>
        item.id === itemId && item.units > 1
          ? { ...item, units: item.units - 1 }
          : item
      );
      saveToLocalStorage();
      publishState();
    },

    removeItem(item) {
      const itemId = item.id;
      store.lineItems = store.lineItems.filter((item) => item.id !== itemId);
      saveToLocalStorage();
      publishState();
    },

    clearCart() {
      store.lineItems = [];
      saveToLocalStorage();
      publishState();
    },

    updateItemQuantity(item, quantity) {
      if (quantity <= 0) {
        this.removeItem(item);
        return;
      }
      const itemId = item.id;
      store.lineItems = store.lineItems.map((item) =>
        item.id === itemId ? { ...item, units: quantity } : item
      );
      saveToLocalStorage();
      publishState();
    },

    toggleCart() {
      store.isCartOpen = !store.isCartOpen;
    },

    openCart() {
      store.isCartOpen = true;
    },

    closeCart() {
      store.isCartOpen = false;
    },

    completeCheckout() {
      bubbleEvent("cart_button_clicked");
      this.closeCart();
    },
  };

  return {
    // Read-only state
    get lineItems() {
      return store.lineItems;
    },
    get currency() {
      return store.currency;
    },
    get taxInclusive() {
      return store.taxInclusive;
    },
    get showTax() {
      return store.showTax;
    },
    get buttonType() {
      return store.buttonType;
    },
    get buttonText() {
      return store.buttonText;
    },
    get itemsIndicator() {
      return store.itemsIndicator;
    },
    get isCartOpen() {
      return store.isCartOpen;
    },

    // Derived values
    get subtotal() {
      return subtotal;
    },
    get tax() {
      return tax;
    },
    get total() {
      return total;
    },
    get itemCount() {
      return itemCount;
    },
    get isEmpty() {
      return isEmpty;
    },

    // Actions
    actions: actions,
  };
}

export const cart = createCartStore();
